import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PackagePlus, ArrowRightLeft, PackageMinus, Boxes, AlertTriangle, Lock, Box } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, GlassCard, GradButton, EmptyState, StatCard } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { StokDialog } from "@/components/StokDialog";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import api from "@/lib/api";

const HAREKET = { giris: ["Giriş", "text-emerald-400"], cikis: ["Çıkış", "text-red-300"], transfer: ["Transfer", "text-sky-300"],
  rezerve: ["Rezerve", "text-amber-300"], rezerve_iptal: ["Rezerve iptal", "text-muted-foreground"], sevk: ["Sevk", "text-violet-300"],
  uretim_sarf: ["Üretim sarfı", "text-orange-300"], uretim_giris: ["Üretimden giriş", "text-emerald-300"] };
const TH = ({ children, r }) => <th className={`p-4 ${r ? "text-right" : ""}`}>{children}</th>;
const Thead = ({ cols }) => (
  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
    {cols.map(([c, r], i) => <TH key={i} r={r}>{c}</TH>)}
  </tr></thead>
);

export default function Depo() {
  const { user, ayar } = useAuth();
  const [ozet, setOzet] = useState([]);
  const [bakiyeler, setBakiyeler] = useState([]);
  const [hareketler, setHareketler] = useState([]);
  const [depolar, setDepolar] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [depoF, setDepoF] = useState("hepsi");
  const [dlg, setDlg] = useState(null);
  const yazabilir = can(user, "stok:write");

  const load = useCallback(async () => {
    const [o, b, h, d, u] = await Promise.all([api.get("/stok/ozet"), api.get("/stok/bakiyeler"), api.get("/stok/hareketler"), api.get("/depolar"), api.get("/urunler")]);
    setOzet(o.data); setBakiyeler(b.data); setHareketler(h.data); setDepolar(d.data); setUrunler(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const kritik = ozet.filter((x) => x.kritik).length;
  const rezerveKalem = ozet.filter((x) => x.rezerve > 0).length;
  const rows = bakiyeler.filter((b) => depoF === "hepsi" || b.depo_id === depoF);

  return (
    <div data-testid="depo-page">
      <PageHeader eyebrow="Operasyon" title="Depo Takip" desc="Canlı stok: fiziksel miktar, siparişe ayrılan (rezerve) ve kullanılabilir. Tüm hareketler kayıt altında.">
        <Link to="/depo-simulasyon" data-testid="depo-simulasyon-link" className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[var(--g1)] text-[var(--g1)] text-sm hover:bg-white/5"><Box className="w-4 h-4" />3D Depo Simülasyonu</Link>
        {yazabilir && <GradButton data-testid="stok-giris-btn" onClick={() => setDlg({ tip: "giris" })}><PackagePlus className="w-4 h-4" />Stok girişi</GradButton>}
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Takipteki ürün" value={ozet.length} icon={Boxes} testid="stat-takip" />
        <StatCard label="Kritik stok" value={kritik} icon={AlertTriangle} testid="stat-kritik" delay={60} />
        <StatCard label="Rezerveli ürün" value={rezerveKalem} icon={Lock} testid="stat-rezerve" delay={120} />
        <StatCard label="Depo" value={depolar.length} icon={ArrowRightLeft} testid="stat-depo" delay={180} />
      </div>
      <Tabs defaultValue="ozet">
        <TabsList className="bg-white/5">
          <TabsTrigger value="ozet" data-testid="depo-tab-ozet">Ürün bazlı</TabsTrigger>
          <TabsTrigger value="bakiye" data-testid="depo-tab-bakiye">Depo / lot bazlı</TabsTrigger>
          <TabsTrigger value="hareket" data-testid="depo-tab-hareket">Hareketler</TabsTrigger>
        </TabsList>
        <TabsContent value="ozet">
          <GlassCard className="overflow-x-auto">
            {ozet.length === 0 ? <EmptyState text="Stok takibi açık ürün yok." /> : (
              <table className="w-full text-sm" data-testid="stok-ozet-tablo">
                <Thead cols={[["Ürün"], ["Fiziksel", 1], ["Rezerve", 1], ["Kullanılabilir", 1], ["Min.", 1], [""]]} />
                <tbody>{ozet.map((x) => (
                  <tr key={x.id} className="border-b border-white/5" data-testid={`stok-ozet-${x.kod}`}>
                    <td className="p-4"><span className="font-mono text-xs text-muted-foreground mr-2">{x.kod}</span>{x.ad}</td>
                    <td className="p-4 text-right font-mono">{x.miktar} <span className="text-muted-foreground text-xs">{x.birim}</span></td>
                    <td className="p-4 text-right font-mono text-amber-300">{x.rezerve}</td>
                    <td className="p-4 text-right font-mono font-semibold" data-testid={`stok-kullanilabilir-${x.kod}`}>{x.kullanilabilir}</td>
                    <td className="p-4 text-right font-mono text-muted-foreground">{x.min_stok || 0}</td>
                    <td className="p-4">{x.kritik && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">kritik</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </GlassCard>
        </TabsContent>
        <TabsContent value="bakiye">
          <div className="w-64 mb-3"><SelectBox testid="depo-filtre" value={depoF} onChange={setDepoF} options={[{ value: "hepsi", label: "Tüm depolar" }, ...depolar.map((d) => ({ value: d.id, label: d.ad }))]} /></div>
          <GlassCard className="overflow-x-auto">
            {rows.length === 0 ? <EmptyState text="Bu depoda stok yok." testid="bakiye-bos" /> : (
              <table className="w-full text-sm" data-testid="bakiye-tablo">
                <Thead cols={[["Ürün"], ["Depo"], ["Lot"], ["SKT"], ["Miktar", 1], ["Rezerve", 1], ["Kullanılabilir", 1], [""]]} />
                <tbody>{rows.map((b) => (
                  <tr key={b.id} className="border-b border-white/5" data-testid={`bakiye-${b.urun_kod}-${b.lot || "lotsuz"}`}>
                    <td className="p-4"><span className="font-mono text-xs text-muted-foreground mr-2">{b.urun_kod}</span>{b.urun_ad}</td>
                    <td className="p-4 text-muted-foreground">{b.depo_ad}{b.konum && <div className="font-mono text-[11px] text-[var(--g1)]" data-testid={`bakiye-konum-${b.urun_kod}`}>{b.konum}</div>}</td>
                    <td className="p-4 font-mono text-xs">{b.lot || "—"}</td>
                    <td className="p-4 text-xs">{b.skt || "—"}</td>
                    <td className="p-4 text-right font-mono">{b.miktar}</td>
                    <td className="p-4 text-right font-mono text-amber-300">{b.rezerve}</td>
                    <td className="p-4 text-right font-mono font-semibold">{b.kullanilabilir}</td>
                    <td className="p-4 text-right whitespace-nowrap">{yazabilir && <>
                      <button title="Transfer" data-testid={`bakiye-transfer-${b.urun_kod}`} onClick={() => setDlg({ tip: "transfer", bakiye: b })} className="p-2 rounded-lg text-muted-foreground hover:text-sky-300"><ArrowRightLeft className="w-4 h-4" /></button>
                      <button title="Çıkış" data-testid={`bakiye-cikis-${b.urun_kod}`} onClick={() => setDlg({ tip: "cikis", bakiye: b })} className="p-2 rounded-lg text-muted-foreground hover:text-red-300"><PackageMinus className="w-4 h-4" /></button>
                    </>}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </GlassCard>
        </TabsContent>
        <TabsContent value="hareket">
          <GlassCard className="overflow-x-auto">
            {hareketler.length === 0 ? <EmptyState text="Henüz hareket yok." /> : (
              <table className="w-full text-sm" data-testid="hareket-tablo">
                <Thead cols={[["Tarih"], ["Tip"], ["Ürün"], ["Depo"], ["Miktar", 1], ["Belge"], ["Kullanıcı"]]} />
                <tbody>{hareketler.map((h) => (
                  <tr key={h.id} className="border-b border-white/5">
                    <td className="p-4 text-xs text-muted-foreground whitespace-nowrap">{new Date(h.tarih).toLocaleString("tr-TR")}</td>
                    <td className={`p-4 text-xs ${HAREKET[h.tip]?.[1]}`}>{HAREKET[h.tip]?.[0] || h.tip}</td>
                    <td className="p-4"><span className="font-mono text-xs text-muted-foreground mr-2">{h.urun_kod}</span>{h.urun_ad}{h.lot ? <span className="text-xs text-muted-foreground"> · lot {h.lot}</span> : null}</td>
                    <td className="p-4 text-xs">{h.depo_ad}{h.hedef_depo_ad ? ` → ${h.hedef_depo_ad}` : ""}</td>
                    <td className="p-4 text-right font-mono">{h.miktar}</td>
                    <td className="p-4 font-mono text-xs">{h.belge || h.aciklama || "—"}</td>
                    <td className="p-4 text-xs text-muted-foreground">{h.kullanici}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
      {dlg && <StokDialog {...dlg} urunler={urunler} depolar={depolar} lotZorunlu={ayar?.lot_skt_zorunlu} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </div>
  );
}
